import dot from 'dot-prop-immutable';
import { pathOr, uniq } from 'ramda';
import reduceReducers from 'reduce-reducers';

import { createEntitiesReducer } from 'modules/common/redux';

import { types as appTypes } from '../data/actions';
import { types } from './actions';

const initialState = {
  accountId: null,
  entities: {},
};

const entitiesReducer = createEntitiesReducer(
  ['accounts', 'billingAddresses', 'contacts', 'facilities'],
  [
    types.ACCOUNT_RECEIVE,
    types.BILLING_ADDRESS_RECEIVE,
    types.CONTACT_RECEIVE,
    types.FACILITY_RECEIVE,
  ],
);

const reducer = (state = initialState, action) => {
  const { payload, type } = action;
  switch (type) {
    case types.ACCOUNT_RECEIVE:
      return {
        ...state,
        accountId: payload.result,
      };
    case types.BILLING_ADDRESS_RECEIVE: {
      const accountId = state.accountId;
      const nextBillingAddressIds = uniq(
        pathOr(
          [],
          ['entities', 'accounts', accountId, 'billingAddresses'],
          state,
        ).concat([payload.result]),
      );
      return dot.set(
        state,
        `entities.accounts.${accountId}.billingAddresses`,
        nextBillingAddressIds,
      );
    }
    case types.FACILITY_RECEIVE: {
      const accountId = state.accountId;
      const nextFacilitiesIds = uniq(
        pathOr(
          [],
          ['entities', 'accounts', accountId, 'facilities'],
          state,
        ).concat([payload.result]),
      );
      return dot.set(
        state,
        `entities.accounts.${accountId}.facilities`,
        nextFacilitiesIds,
      );
    }
    case appTypes.ADD_CONTACT_SUCCESS: {
      const accountId = state.accountId;
      const nextContactIds = uniq(
        pathOr(
          [],
          ['entities', 'accounts', accountId, 'contacts'],
          state,
        ).concat([payload.contactId]),
      );
      return dot.set(
        state,
        `entities.accounts.${accountId}.contacts`,
        nextContactIds,
      );
    }
    case appTypes.DELETE_BILLING_ADDRESS_SUCCESS: {
      const accountId = state.accountId;
      const nextBillingAddressIds = pathOr(
        [],
        ['entities', 'accounts', accountId, 'billingAddresses'],
        state,
      ).filter(
        (billingAddressId) => billingAddressId != payload.billingAddressId,
      );
      return dot.set(
        state,
        `entities.accounts.${accountId}.billingAddresses`,
        nextBillingAddressIds,
      );
    }
    case appTypes.DELETE_FACILITY_SUCCESS: {
      const accountId = state.accountId;
      const nextFacilitiesIds = pathOr(
        [],
        ['entities', 'accounts', accountId, 'facilities'],
        state,
      ).filter((facilityId) => facilityId != payload.facilityId);
      return dot.set(
        state,
        `entities.accounts.${accountId}.facilities`,
        nextFacilitiesIds,
      );
    }
    case appTypes.REMOVE_CONTACT_SUCCESS: {
      const accountId = state.accountId;
      const nextContactIds = pathOr(
        [],
        ['entities', 'accounts', accountId, 'contacts'],
        state,
      ).filter((contactId) => contactId != payload.contactId);
      return dot.set(
        state,
        `entities.accounts.${accountId}.contacts`,
        nextContactIds,
      );
    }
    default:
      return state;
  }
};

const getScope = (state) => state.modules.contacts.accountDetails.data;

export const getAccount = (state) =>
  getScope(state)?.entities?.accounts?.[getScope(state).accountId] ?? null;

export const getBillingAddresses = (state) =>
  pathOr([], ['billingAddresses'], getAccount(state)).map((billingAddressId) =>
    pathOr(
      null,
      ['entities', 'billingAddresses', billingAddressId],
      getScope(state),
    ),
  );

export const getContacts = (state) =>
  getAccount(state)?.contacts?.map(
    (contactId) => getScope(state)?.entities?.contacts?.[contactId],
  ) ?? [];

export const getFacilities = (state) =>
  pathOr([], ['facilities'], getAccount(state)).map((facilityId) =>
    pathOr(null, ['entities', 'facilities', facilityId], getScope(state)),
  );

export default reduceReducers(entitiesReducer, reducer, initialState);
